<template>
  <!-- Общий контент экрана поиска (мобильный полноэкранный поиск и левая панель
       десктопа): поле, под ним при пустом поле «Недавние» и «Маршруты рядом»,
       при вводе - подсказки. Текст и прокрутка живут в searchSession, чтобы
       «Назад» из карточки объекта вернул поиск в том же виде. -->
  <div class="flex flex-col h-full min-h-0">
    <div class="px-3 pt-3 pb-2 shrink-0" @focusout="onFieldFocusOut">
      <SearchField
        ref="fieldRef"
        v-model="session.query"
        :active="cancelVisible"
        @focus="onFocus"
        @cancel="onCancel"
        @keydown="onKeydown"
      />
    </div>

    <div ref="scrollEl" data-search-scroll class="flex-1 min-h-0 overflow-y-auto" @scroll="onScroll" @touchmove.passive="onUserScroll" @wheel.passive="onUserScroll">
      <template v-if="search.active.value">
        <div v-if="search.error.value" data-search-error role="alert" class="mx-3 my-2 flex items-center justify-between gap-3 rounded-md bg-paper2 px-3 py-2 text-[13px]">
          <span class="text-ink2">{{ t('search.error') }}</span>
          <button type="button" data-search-retry class="shrink-0 font-semibold text-forest" @click="search.retry()">{{ t('search.retry') }}</button>
        </div>
        <p v-if="search.loading.value && !search.result.value" class="px-3 py-6 text-center text-xs text-ink2">{{ t('search.loading') }}</p>
        <template v-else-if="search.result.value">
          <div v-if="hasHits" class="flex justify-end px-4 pt-1">
            <DistanceOriginToggle />
          </div>
          <SearchResults v-if="hasHits" ref="resultsRef" :result="search.result.value" :distance-for="search.distanceFor" @pick="onPickHit" />
          <div v-else-if="!search.error.value" data-no-results class="px-6 py-6 text-center">
            <p class="text-[14px] font-semibold">{{ t('search.noResults') }}</p>
            <p class="mt-1 text-[12px] text-ink2">{{ t('search.noResultsHint') }}</p>
          </div>
        </template>
      </template>

      <template v-else>
        <RecentList @pick-query="onPickQuery" @open-object="onOpenRecent" />

        <section v-if="routesLoading || routesStore.error || nearby.length" data-nearby class="px-3 pb-6">
          <div class="flex items-center justify-between gap-2 px-1 pt-2 pb-2">
            <h3 class="text-[11px] font-semibold text-ink2">{{ t('search.nearbyRoutes') }}</h3>
            <DistanceOriginToggle />
          </div>
          <div v-if="routesStore.error && !routesLoading" data-routes-error role="alert" class="flex items-center justify-between gap-3 rounded-md bg-paper2 px-3 py-2 text-[13px]">
            <span class="text-ink2">{{ t('search.routesError') }}</span>
            <button type="button" data-routes-retry class="shrink-0 font-semibold text-forest" @click="routesStore.retry()">{{ t('search.retry') }}</button>
          </div>
          <div v-else-if="routesLoading" class="grid gap-3" :aria-busy="true">
            <span class="sr-only" role="status">{{ t('search.loadingRoutes') }}</span>
            <div
              v-for="i in NEARBY_ROUTES_COUNT"
              :key="i"
              data-route-skeleton
              aria-hidden="true"
              class="h-[260px] rounded-xl bg-[hsl(var(--muted)/0.5)] animate-pulse"
            />
          </div>
          <div v-else class="grid gap-3">
            <RouteCardLarge
              v-for="n in nearby"
              :key="n.route.id"
              :route="n.route"
              :distance-km="n.meters != null ? n.meters / 1000 : null"
              :saved="savedRoutes.isSaved(n.route.id)"
              :authed="auth.isAuthenticated"
              @open="onOpenNearby"
              @toggle-save="toggleSave"
              @login-to-save="(id) => loginGate.open(() => savedRoutes.save(id))"
            />
          </div>
          <a
            v-if="!routesLoading"
            href="/map?view=list"
            data-all-routes
            class="mt-3 block px-1 text-[14px] font-semibold text-forest"
            @click.prevent="openCatalogue"
          >{{ t('search.allRoutes') }}</a>
        </section>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import SearchField from "./SearchField.vue";
import SearchResults from "./SearchResults.vue";
import RecentList from "./RecentList.vue";
import DistanceOriginToggle from "./DistanceOriginToggle.vue";
import RouteCardLarge from "../route/RouteCardLarge.vue";
import { useSearchableRoutes } from "../../composables/useSearchableRoutes";
import { createApiProvider } from "../../search/apiProvider";
import { useSearch } from "../../search/useSearch";
import { kindLabel } from "../../search/kinds";
import { pickLocalizedName } from "../../search/names";
import { NEARBY_ROUTES_COUNT, nearbyRoutes } from "../../search/nearbyRoutes";
import type { SearchHit } from "../../search/types";
import { useSearchHistory, type HistoryEntry, type HistoryObjectInput } from "../../composables/useSearchHistory";
import { replaceOrPush } from "../../composables/searchHistoryEntry";
import { useSearchSessionStore } from "../../store/searchSession";
import { useDistanceOriginStore } from "../../store/distanceOrigin";
import { useRoutesStore } from "../../store/routes";
import { useSavedRoutesStore } from "../../store/savedRoutes";
import { useAuthStore } from "../../store/auth";
import { useLoginGateStore } from "../../store/loginGate";
import type { RouteMapItem } from "../../types/routes";
import { trackEvent } from "../../analytics";

const props = defineProps<{ variant: "mobile" | "desktop" }>();
const emit = defineEmits<{
  (e: "open-route", routeId: number): void;
  (e: "open-point", id: string): void;
  (e: "cancel"): void;
}>();

const { t, te, locale } = useI18n();
const router = useRouter();
const session = useSearchSessionStore();
const history = useSearchHistory();
const routesStore = useRoutesStore();
const routes = useSearchableRoutes();
const savedRoutes = useSavedRoutesStore();
const auth = useAuthStore();
const loginGate = useLoginGateStore();
const { origin } = storeToRefs(useDistanceOriginStore());

const search = useSearch(createApiProvider(() => routes.value), { origin });
watch(() => session.query, (q) => (search.query.value = q), { immediate: true });

const fieldRef = ref<InstanceType<typeof SearchField> | null>(null);
const resultsRef = ref<InstanceType<typeof SearchResults> | null>(null);
const scrollEl = ref<HTMLElement | null>(null);

// Мобильный поиск всегда развёрнут - «Отмена» видна сразу; на десктопе - когда
// в поле есть текст или курсор.
const focused = ref(false);
// Фокус ушёл за пределы поля (не на его же «Отмену»/«очистить») - курсора нет.
const onFieldFocusOut = (e: FocusEvent) => {
  const to = e.relatedTarget as Node | null;
  if (!to || !(e.currentTarget as HTMLElement).contains(to)) focused.value = false;
};
// Десктоп: панель видна всегда, фокус только ставит курсор. session.open не
// трогаем (это полноэкранный поиск телефона - всплыл бы при сужении окна) и
// центр не замораживаем: расстояния следуют за живым центром карты. Событие
// «Route: Search» - один раз до «Отмены»/выбора объекта. На телефоне сессию
// (со снимком центра) открывает поле-триггер в покое.
let tracked = false;
const onFocus = () => {
  focused.value = true;
  if (props.variant === "desktop" && !tracked) {
    tracked = true;
    trackEvent("Route: Search");
  }
};
const cancelVisible = computed(() => props.variant === "mobile" || focused.value || !!session.query);

const hasHits = computed(() => {
  const r = search.result.value;
  return !!r && r.places.length + r.routes.length + r.pois.length > 0;
});

const routesLoading = computed(() => routesStore.loading || !routesStore.loaded);
const nearby = computed(() => nearbyRoutes(routes.value, origin.value, NEARBY_ROUTES_COUNT));
const toggleSave = (id: number) => (savedRoutes.isSaved(id) ? savedRoutes.unsave(id) : savedRoutes.save(id));

const blurField = () => {
  fieldRef.value?.blur();
  focused.value = false;
};

// Каталог - выход из поиска: сессия закрывается до перехода; запись
// полноэкранного поиска (телефон) заменяется каталогом, не остаётся позади.
const openCatalogue = () => {
  session.cancel();
  blurField();
  void replaceOrPush(router, "/map?view=list");
};

const onCancel = () => {
  tracked = false;
  session.cancel();
  blurField();
  emit("cancel");
};

// Enter: на выделенной стрелками подсказке - открыть её (SearchResults
// отменяет событие); иначе - записать запрос и спрятать клавиатуру, подсказки остаются.
const onKeydown = (e: KeyboardEvent) => {
  if (search.active.value) resultsRef.value?.handleKey(e);
  if (e.key !== "Enter" || e.defaultPrevented) return;
  history.addQuery(session.query);
  blurField();
};

const routeSubtitle = (km: number | null | undefined) =>
  km != null ? t("search.routeSubtitle", { km: km.toLocaleString(locale.value, { maximumFractionDigits: 1 }) }) : t("search.groupRoutes");

const pointSubtitle = (osmKind: string, ele?: number) =>
  [kindLabel(osmKind, t, te), ele != null ? t("search.elevation", { m: Math.round(ele) }) : ""].filter(Boolean).join(" · ");

// Открыть объект из поиска: в «Недавние» ложится объект (не текст запроса),
// поиск запоминает, что «Назад» в карточке ведёт сюда.
const openObject = (entry: HistoryObjectInput) => {
  tracked = false;
  history.addObject(entry);
  session.markOpenedFromSearch();
  blurField();
  if (entry.kind === "route" && entry.routeId != null) emit("open-route", entry.routeId);
  else emit("open-point", entry.id);
};

const onPickHit = (hit: SearchHit) => {
  if (hit.kind === "route") {
    const [lat, lon] = hit.line[0] ?? [null, null];
    openObject({ kind: "route", id: hit.id, routeId: hit.routeId, name: hit.name, subtitle: routeSubtitle(hit.distanceKm), lat, lon });
    return;
  }
  openObject({
    kind: hit.kind,
    id: hit.id,
    name: pickLocalizedName(hit.names, hit.name, locale.value) || kindLabel(hit.osmKind, t, te),
    subtitle: pointSubtitle(hit.osmKind, hit.ele),
    lat: hit.lat,
    lon: hit.lon,
    osmKind: hit.osmKind,
  });
};

const onOpenNearby = (id: number) => {
  const r = routes.value.find((x) => x.id === id);
  if (!r) return;
  openObject(routeEntry(r));
};
const routeEntry = (r: RouteMapItem): HistoryObjectInput => {
  const first = r.preview_line?.[0];
  const lat = first ? first[1] : r.start_lat ?? null;
  const lon = first ? first[0] : r.start_lon ?? null;
  return { kind: "route", id: `route:${r.id}`, routeId: r.id, name: r.name, subtitle: routeSubtitle(r.distance_km), lat, lon };
};

const onOpenRecent = (e: Extract<HistoryEntry, { type: "object" }>) => {
  const { type: _type, at: _at, ...entry } = e;
  openObject(entry);
};

// Недавний запрос подставляется в поле и ищется заново.
const onPickQuery = (text: string) => {
  session.query = text;
  history.addQuery(text);
};

// Прокрутка: запоминаем для возврата из карточки. Клавиатуру на телефоне
// прячет только жест человека (touchmove / колесо), не событие scroll: его шлёт
// и браузер, когда контент укоротился (подсказки вместо «Маршрутов рядом») и
// scrollTop обрезался, - это закрывало бы клавиатуру посреди набора.
let restoring = false;
const onScroll = () => {
  const el = scrollEl.value;
  if (!el || restoring) return;
  session.rememberScroll(el.scrollTop);
};
const onUserScroll = () => {
  if (props.variant === "mobile") blurField();
};
// Смена блоков (пустое поле <-> подсказки) - другой контент: с начала.
watch(
  () => search.active.value,
  () => {
    const el = scrollEl.value;
    if (!el) return;
    restoring = true;
    el.scrollTop = 0;
    session.rememberScroll(0);
    requestAnimationFrame(() => (restoring = false));
  }
);
const restoreScroll = async () => {
  await nextTick();
  const el = scrollEl.value;
  if (!el || !session.scrollTop) return;
  restoring = true;
  el.scrollTop = session.scrollTop;
  requestAnimationFrame(() => (restoring = false));
};
// Контент появляется асинхронно (маршруты, ответ поиска) - восстанавливаем,
// когда он есть, один раз за монтирование.
let restored = false;
const tryRestore = () => {
  if (restored) return;
  const ready = search.active.value ? !!search.result.value : !routesLoading.value;
  if (!ready) return;
  restored = true;
  void restoreScroll();
};
watch([() => search.result.value, routesLoading], tryRestore);

onMounted(() => {
  routesStore.fetch();
  tryRestore();
});

defineExpose({ focus: () => fieldRef.value?.focus(), blur: blurField });
</script>
